import Handlebars from 'handlebars';
import dayjs from 'dayjs';
// Add plugin imports as default imports and extend dayjs so plugins are functions (not namespace objects)
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Ensure utc is extended before timezone
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export function registerHandlerbarHelpers() {
  Handlebars.registerHelper('formatDate', function (date, format) {
    return new Handlebars.SafeString(dayjs(date).format(format));
  });

  Handlebars.registerHelper('creditDebitSign', function (type) {
    if (type === 'credit') {
      return '+';
    } else if (type === 'debit') {
      return '-';
    }
    return '';
  });

  Handlebars.registerHelper('formatTerm', function (months: number) {
    if (months % 12 === 0) {
      const years = months / 12;
      return years === 1 ? '1 Year' : `${years} Years`;
    }
    return `${months} Months`;
  });

  Handlebars.registerHelper('formatMask', function format(value, pattern) {
    if (value?.toString()?.includes('skeleton_item')) {
      return value.toString();
    }
    let i = 0;
    const v = value.toString();
    if (value != '') {
      return pattern.replace(/#/g, () => v[i++]);
    }

    return '-';
  });

  Handlebars.registerHelper('currencySymbol', function format(value) {
    return currencyToNarrowSymbolMap[value];
  });

  Handlebars.registerHelper('formatAmount', formatAmountFunction);

  Handlebars.registerHelper('resizeLogo', function (svgContent, width, height) {
    const svgString = svgContent.replace('<svg', `<svg width="${width}" height="${height}"`);

    return new Handlebars.SafeString(svgString);
  });

  Handlebars.registerHelper('isNotEmpty', function (value) {
    return value !== undefined && value !== null && value.toString().trim() !== '';
  });

  Handlebars.registerHelper(
    'relativeDateFormat',
    function (
      date: string | Date,
      grouping: 'day' | 'week' | 'month' | 'year',
      format: string,
      isRelativeDate = false,
    ) {
      const inputDate = dayjs(date);
      const now = dayjs();

      if (!inputDate.isValid()) {
        return '';
      }

      if (isRelativeDate) {
        // Handle relative date formats
        if (grouping === 'day') {
          if (inputDate.isSame(now, 'day')) {
            return 'Today';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
            return 'Yesterday';
          }
        } else if (grouping === 'week') {
          if (inputDate.isSame(now, 'week')) {
            return 'This Week';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'week'), 'week')) {
            return 'Last Week';
          }
        } else if (grouping === 'month') {
          if (inputDate.isSame(now, 'month')) {
            return 'This Month';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'month'), 'month')) {
            return 'Last Month';
          }
        }
      }

      // Check if the format exists for the given grouping
      if (!DATE_FORMATS[grouping]?.[format]) {
        return inputDate.format('DD MMM YYYY'); // Default format
      }

      // Special handling for week formats
      if (grouping === 'week') {
        const weekStart = inputDate.clone().startOf('week');
        const weekEnd = inputDate.clone().endOf('week');

        const formatMap: Record<string, string> = {
          'Week of DD MMM YYYY': `Week of ${weekStart.format('DD MMM YYYY')}`,
          'DD MMM - DD MMM YYYY': `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`,
          'MMM DD - DD, YYYY': `${weekStart.format('MMM DD')} - ${weekEnd.format('DD, YYYY')}`,
          'Week of DD MMMM YYYY': `Week of ${weekStart.format('DD MMMM YYYY')}`,
          'DD MMMM - DD MMMM YYYY': `${weekStart.format('DD MMMM')} - ${weekEnd.format('DD MMMM YYYY')}`,
          'MMMM DD - DD, YYYY': `${weekStart.format('MMMM DD')} - ${weekEnd.format('DD, YYYY')}`,
        };

        return formatMap[format] || formatMap['Week of DD MMM YYYY'];
      }

      // For day, month, and year groupings, use the format directly
      return inputDate.format(DATE_FORMATS[grouping][format]);
    },
  );

  registerComparisonHelpers();
}

export const formatAmountFunction = function formatAmountFunction(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  amount: any,
  currency = 'GBP',
  locale = 'en-US',
) {
  // helps handle if only one arg given in handlebar template
  if (typeof currency === 'object') {
    currency = 'GBP';
    locale = 'en-US';
  }
  // helps handle if only two args given in handlebar template
  if (typeof locale === 'object') {
    locale = 'en-US';
  }

  // Get the correct fractional digits format but strip currency to not print a currency symbol
  let options = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).resolvedOptions();
  options = {
    ...options,
    style: 'decimal',
    currency: undefined,
  };

  // Check if amount is a valid number
  if (!isNaN(parseFloat(amount)) && isFinite(amount)) {
    return new Intl.NumberFormat(locale ?? 'en-US', options).format(amount);
  } else {
    // If not a valid number, return as it is
    return amount;
  }
};

export function registerComparisonHelpers() {
  Handlebars.registerHelper('eq', eq);
  Handlebars.registerHelper('and', and);
  Handlebars.registerHelper('gt', gt);
  Handlebars.registerHelper('gte', gte);
  Handlebars.registerHelper('lt', lt);
  Handlebars.registerHelper('lte', lte);
  Handlebars.registerHelper('not', not);
  Handlebars.registerHelper('seq', seq);
  Handlebars.registerHelper('xor', xor);
  Handlebars.registerHelper('neq', neq);
  Handlebars.registerHelper('or', or);
  Handlebars.registerHelper('stringPropertySet', stringPropertySet);
  Handlebars.registerHelper('isMultipleOfTwo', function (index) {
    return index % 2 === 0;
  });
}

export function eq(...params: any[]) {
  if (params[2] === 'obj') {
    return JSON.stringify(params[0]) === JSON.stringify(params[1]);
  }
  return params[0] == params[1];
}

export function and(...params: any[]) {
  return params[0] && params[1];
}

export function or(...params: any[]) {
  return params[0] || params[1];
}

export function gt(...params: any[]) {
  if (params[1] != null && typeof params[1] === 'number') {
    return params[0] > params[1];
  } else if (typeof params[1] == 'string') {
    return parseFloat(params[0]) > parseFloat(params[1]);
  }
  return;
}

export function gte(...params: any[]) {
  if (params[1] != null && typeof params[1] === 'number') {
    return params[0] >= params[1];
  } else if (typeof params[1] == 'string') {
    return parseFloat(params[0]) >= parseFloat(params[1]);
  }
  return;
}

export function lt(...params: any[]) {
  if (params[1] != null && typeof params[1] === 'number') {
    return params[0] < params[1];
  } else if (typeof params[1] == 'string') {
    return parseFloat(params[0]) < parseFloat(params[1]);
  }
  return;
}

export function lte(...params: any[]) {
  if (params[1] != null && typeof params[1] === 'number') {
    return params[0] <= params[1];
  } else if (typeof params[1] == 'string') {
    return parseFloat(params[0]) <= parseFloat(params[1]);
  }
  return;
}

export function not(...params: any[]) {
  return !params[0];
}

export function seq(...params: any[]) {
  return params[0] === params[1];
}

export function xor(...params: any[]) {
  return (params[0] && !params[1]) || (!params[0] && params[1]);
}
export function neq(...params: any[]) {
  return params[0] !== params[1];
}

export function stringPropertySet(inputString: string): boolean {
  if (inputString) {
    return true;
  } else {
    return false;
  }
}

export const currencyToNarrowSymbolMap: { [currencyCode: string]: string } = {
  AED: 'د.إ',
  AFN: '؋',
  ALL: 'L',
  AMD: '֏',
  ANG: 'ƒ',
  AOA: 'Kz',
  ARS: '$',
  AUD: '$',
  AWG: 'ƒ',
  AZN: '₼',
  BAM: 'KM',
  BBD: '$',
  BDT: '৳',
  BGN: 'лв',
  BHD: '.د.ب',
  BIF: 'FBu',
  BMD: '$',
  BND: '$',
  BOB: '$b',
  BOV: 'BOV',
  BRL: 'R$',
  BSD: '$',
  BTC: '₿',
  BTN: 'Nu.',
  BWP: 'P',
  BYN: 'Br',
  BYR: 'Br',
  BZD: 'BZ$',
  CAD: '$',
  CDF: 'FC',
  CHE: 'CHE',
  CHF: 'CHF',
  CHW: 'CHW',
  CLF: 'CLF',
  CLP: '$',
  CNH: '¥',
  CNY: '¥',
  COP: '$',
  COU: 'COU',
  CRC: '₡',
  CUC: '$',
  CUP: '₱',
  CVE: '$',
  CZK: 'Kč',
  DJF: 'Fdj',
  DKK: 'kr',
  DOP: 'RD$',
  DZD: 'دج',
  EEK: 'kr',
  EGP: '£',
  ERN: 'Nfk',
  ETB: 'Br',
  ETH: 'Ξ',
  EUR: '€',
  FJD: '$',
  FKP: '£',
  GBP: '£',
  GEL: '₾',
  GGP: '£',
  GHC: '₵',
  GHS: 'GH₵',
  GIP: '£',
  GMD: 'D',
  GNF: 'FG',
  GTQ: 'Q',
  GYD: '$',
  HKD: '$',
  HNL: 'L',
  HRK: 'kn',
  HTG: 'G',
  HUF: 'Ft',
  IDR: 'Rp',
  ILS: '₪',
  IMP: '£',
  INR: '₹',
  IQD: 'ع.د',
  IRR: '﷼',
  ISK: 'kr',
  JEP: '£',
  JMD: 'J$',
  JOD: 'JD',
  JPY: '¥',
  KES: 'KSh',
  KGS: 'лв',
  KHR: '៛',
  KMF: 'CF',
  KPW: '₩',
  KRW: '₩',
  KWD: 'KD',
  KYD: '$',
  KZT: '₸',
  LAK: '₭',
  LBP: '£',
  LKR: '₨',
  LRD: '$',
  LSL: 'M',
  LTC: 'Ł',
  LTL: 'Lt',
  LVL: 'Ls',
  LYD: 'LD',
  MAD: 'MAD',
  MDL: 'lei',
  MGA: 'Ar',
  MKD: 'ден',
  MMK: 'K',
  MNT: '₮',
  MOP: 'MOP$',
  MRO: 'UM',
  MRU: 'UM',
  MUR: '₨',
  MVR: 'Rf',
  MWK: 'MK',
  MXN: '$',
  MXV: 'MXV',
  MYR: 'RM',
  MZN: 'MT',
  NAD: '$',
  NGN: '₦',
  NIO: 'C$',
  NOK: 'kr',
  NPR: '₨',
  NZD: '$',
  OMR: '﷼',
  PAB: 'B/.',
  PEN: 'S/.',
  PGK: 'K',
  PHP: '₱',
  PKR: '₨',
  PLN: 'zł',
  PYG: 'Gs',
  QAR: '﷼',
  RMB: '￥',
  RON: 'lei',
  RSD: 'Дин.',
  RUB: '₽',
  RWF: 'R₣',
  SAR: '﷼',
  SBD: '$',
  SCR: '₨',
  SDG: 'ج.س.',
  SEK: 'kr',
  SGD: 'S$',
  SHP: '£',
  SLL: 'Le',
  SOS: 'S',
  SRD: '$',
  SSP: '£',
  STD: 'Db',
  STN: 'Db',
  SVC: '$',
  SYP: '£',
  SZL: 'E',
  THB: '฿',
  TJS: 'SM',
  TMT: 'T',
  TND: 'د.ت',
  TOP: 'T$',
  TRL: '₤',
  TRY: '₺',
  TTD: 'TT$',
  TVD: '$',
  TWD: 'NT$',
  TZS: 'TSh',
  UAH: '₴',
  UGX: 'USh',
  USD: '$',
  UYI: 'UYI',
  UYU: '$U',
  UYW: 'UYW',
  UZS: 'лв',
  VEF: 'Bs',
  VES: 'Bs.S',
  VND: '₫',
  VUV: 'VT',
  WST: 'WS$',
  XAF: 'FCFA',
  XBT: 'Ƀ',
  XCD: '$',
  XOF: 'CFA',
  XPF: '₣',
  XSU: 'Sucre',
  XUA: 'XUA',
  YER: '﷼',
  ZAR: 'R',
  ZMW: 'ZK',
  ZWD: 'Z$',
  ZWL: '$',
};

/**
 * Converts ISO 3166-1 alpha-3 country code to ISO 3166-1 alpha-2 country code
 *
 * @param alpha3Code - The 3-letter country code to convert
 * @returns The corresponding 2-letter country code or the original code if not found
 */
export const convertAlpha3ToAlpha2CountryCode: Record<string, string> = {
  AFG: 'AF', // Afghanistan
  ALB: 'AL', // Albania
  DZA: 'DZ', // Algeria
  ASM: 'AS', // American Samoa
  AND: 'AD', // Andorra
  AGO: 'AO', // Angola
  AIA: 'AI', // Anguilla
  ATA: 'AQ', // Antarctica
  ATG: 'AG', // Antigua and Barbuda
  ARG: 'AR', // Argentina
  ARM: 'AM', // Armenia
  ABW: 'AW', // Aruba
  AUS: 'AU', // Australia
  AUT: 'AT', // Austria
  AZE: 'AZ', // Azerbaijan
  BHS: 'BS', // Bahamas
  BHR: 'BH', // Bahrain
  BGD: 'BD', // Bangladesh
  BRB: 'BB', // Barbados
  BLR: 'BY', // Belarus
  BEL: 'BE', // Belgium
  BLZ: 'BZ', // Belize
  BEN: 'BJ', // Benin
  BMU: 'BM', // Bermuda
  BTN: 'BT', // Bhutan
  BOL: 'BO', // Bolivia
  BIH: 'BA', // Bosnia and Herzegovina
  BWA: 'BW', // Botswana
  BVT: 'BV', // Bouvet Island
  BRA: 'BR', // Brazil
  IOT: 'IO', // British Indian Ocean Territory
  BRN: 'BN', // Brunei Darussalam
  BGR: 'BG', // Bulgaria
  BFA: 'BF', // Burkina Faso
  BDI: 'BI', // Burundi
  KHM: 'KH', // Cambodia
  CMR: 'CM', // Cameroon
  CAN: 'CA', // Canada
  CPV: 'CV', // Cape Verde
  CYM: 'KY', // Cayman Islands
  CAF: 'CF', // Central African Republic
  TCD: 'TD', // Chad
  CHL: 'CL', // Chile
  CHN: 'CN', // China
  CXR: 'CX', // Christmas Island
  CCK: 'CC', // Cocos (Keeling) Islands
  COL: 'CO', // Colombia
  COM: 'KM', // Comoros
  COG: 'CG', // Congo
  COD: 'CD', // Congo, Democratic Republic of the
  COK: 'CK', // Cook Islands
  CRI: 'CR', // Costa Rica
  CIV: 'CI', // Cote D'Ivoire
  HRV: 'HR', // Croatia
  CUB: 'CU', // Cuba
  CYP: 'CY', // Cyprus
  CZE: 'CZ', // Czech Republic
  DNK: 'DK', // Denmark
  DJI: 'DJ', // Djibouti
  DMA: 'DM', // Dominica
  DOM: 'DO', // Dominican Republic
  ECU: 'EC', // Ecuador
  EGY: 'EG', // Egypt
  SLV: 'SV', // El Salvador
  GNQ: 'GQ', // Equatorial Guinea
  ERI: 'ER', // Eritrea
  EST: 'EE', // Estonia
  ETH: 'ET', // Ethiopia
  FLK: 'FK', // Falkland Islands (Malvinas)
  FRO: 'FO', // Faroe Islands
  FJI: 'FJ', // Fiji
  FIN: 'FI', // Finland
  FRA: 'FR', // France
  GUF: 'GF', // French Guiana
  PYF: 'PF', // French Polynesia
  ATF: 'TF', // French Southern Territories
  GAB: 'GA', // Gabon
  GMB: 'GM', // Gambia
  GEO: 'GE', // Georgia
  DEU: 'DE', // Germany
  GHA: 'GH', // Ghana
  GIB: 'GI', // Gibraltar
  GRC: 'GR', // Greece
  GRL: 'GL', // Greenland
  GRD: 'GD', // Grenada
  GLP: 'GP', // Guadeloupe
  GUM: 'GU', // Guam
  GTM: 'GT', // Guatemala
  GGY: 'GG', // Guernsey
  GIN: 'GN', // Guinea
  GNB: 'GW', // Guinea-Bissau
  GUY: 'GY', // Guyana
  HTI: 'HT', // Haiti
  HMD: 'HM', // Heard Island and Mcdonald Islands
  VAT: 'VA', // Holy See (Vatican City State)
  HND: 'HN', // Honduras
  HKG: 'HK', // Hong Kong
  HUN: 'HU', // Hungary
  ISL: 'IS', // Iceland
  IND: 'IN', // India
  IDN: 'ID', // Indonesia
  IRN: 'IR', // Iran, Islamic Republic Of
  IRQ: 'IQ', // Iraq
  IRL: 'IE', // Ireland
  IMN: 'IM', // Isle of Man
  ISR: 'IL', // Israel
  ITA: 'IT', // Italy
  JAM: 'JM', // Jamaica
  JPN: 'JP', // Japan
  JEY: 'JE', // Jersey
  JOR: 'JO', // Jordan
  KAZ: 'KZ', // Kazakhstan
  KEN: 'KE', // Kenya
  KIR: 'KI', // Kiribati
  PRK: 'KP', // Korea, Democratic People's Republic of
  KOR: 'KR', // Korea, Republic of
  KWT: 'KW', // Kuwait
  KGZ: 'KG', // Kyrgyzstan
  LAO: 'LA', // Lao People's Democratic Republic
  LVA: 'LV', // Latvia
  LBN: 'LB', // Lebanon
  LSO: 'LS', // Lesotho
  LBR: 'LR', // Liberia
  LBY: 'LY', // Libyan Arab Jamahiriya
  LIE: 'LI', // Liechtenstein
  LTU: 'LT', // Lithuania
  LUX: 'LU', // Luxembourg
  MAC: 'MO', // Macao
  MKD: 'MK', // Macedonia, The Former Yugoslav Republic of
  MDG: 'MG', // Madagascar
  MWI: 'MW', // Malawi
  MYS: 'MY', // Malaysia
  MDV: 'MV', // Maldives
  MLI: 'ML', // Mali
  MLT: 'MT', // Malta
  MHL: 'MH', // Marshall Islands
  MTQ: 'MQ', // Martinique
  MRT: 'MR', // Mauritania
  MUS: 'MU', // Mauritius
  MYT: 'YT', // Mayotte
  MEX: 'MX', // Mexico
  FSM: 'FM', // Micronesia, Federated States of
  MDA: 'MD', // Moldova, Republic of
  MCO: 'MC', // Monaco
  MNG: 'MN', // Mongolia
  MNE: 'ME', // Montenegro
  MSR: 'MS', // Montserrat
  MAR: 'MA', // Morocco
  MOZ: 'MZ', // Mozambique
  MMR: 'MM', // Myanmar
  NAM: 'NA', // Namibia
  NRU: 'NR', // Nauru
  NPL: 'NP', // Nepal
  NLD: 'NL', // Netherlands
  ANT: 'AN', // Netherlands Antilles
  NCL: 'NC', // New Caledonia
  NZL: 'NZ', // New Zealand
  NIC: 'NI', // Nicaragua
  NER: 'NE', // Niger
  NGA: 'NG', // Nigeria
  NIU: 'NU', // Niue
  NFK: 'NF', // Norfolk Island
  MNP: 'MP', // Northern Mariana Islands
  NOR: 'NO', // Norway
  OMN: 'OM', // Oman
  PAK: 'PK', // Pakistan
  PLW: 'PW', // Palau
  PSE: 'PS', // Palestinian Territory, Occupied
  PAN: 'PA', // Panama
  PNG: 'PG', // Papua New Guinea
  PRY: 'PY', // Paraguay
  PER: 'PE', // Peru
  PHL: 'PH', // Philippines
  PCN: 'PN', // Pitcairn
  POL: 'PL', // Poland
  PRT: 'PT', // Portugal
  PRI: 'PR', // Puerto Rico
  QAT: 'QA', // Qatar
  REU: 'RE', // Reunion
  ROU: 'RO', // Romania
  RUS: 'RU', // Russian Federation
  RWA: 'RW', // Rwanda
  BLM: 'BL', // Saint Barthelemy
  SHN: 'SH', // Saint Helena
  KNA: 'KN', // Saint Kitts and Nevis
  LCA: 'LC', // Saint Lucia
  MAF: 'MF', // Saint Martin
  SPM: 'PM', // Saint Pierre and Miquelon
  VCT: 'VC', // Saint Vincent and the Grenadines
  WSM: 'WS', // Samoa
  SMR: 'SM', // San Marino
  STP: 'ST', // Sao Tome and Principe
  SAU: 'SA', // Saudi Arabia
  SEN: 'SN', // Senegal
  SRB: 'RS', // Serbia
  SYC: 'SC', // Seychelles
  SLE: 'SL', // Sierra Leone
  SGP: 'SG', // Singapore
  SVK: 'SK', // Slovakia
  SVN: 'SI', // Slovenia
  SLB: 'SB', // Solomon Islands
  SOM: 'SO', // Somalia
  ZAF: 'ZA', // South Africa
  SGS: 'GS', // South Georgia and the South Sandwich Islands
  ESP: 'ES', // Spain
  LKA: 'LK', // Sri Lanka
  SDN: 'SD', // Sudan
  SUR: 'SR', // Suriname
  SJM: 'SJ', // Svalbard and Jan Mayen
  SWZ: 'SZ', // Swaziland
  SWE: 'SE', // Sweden
  CHE: 'CH', // Switzerland
  SYR: 'SY', // Syrian Arab Republic
  TWN: 'TW', // Taiwan
  TJK: 'TJ', // Tajikistan
  TZA: 'TZ', // Tanzania, United Republic of
  THA: 'TH', // Thailand
  TLS: 'TL', // Timor-Leste
  TGO: 'TG', // Togo
  TKL: 'TK', // Tokelau
  TON: 'TO', // Tonga
  TTO: 'TT', // Trinidad and Tobago
  TUN: 'TN', // Tunisia
  TUR: 'TR', // Turkey
  TKM: 'TM', // Turkmenistan
  TCA: 'TC', // Turks and Caicos Islands
  TUV: 'TV', // Tuvalu
  UGA: 'UG', // Uganda
  UKR: 'UA', // Ukraine
  ARE: 'AE', // United Arab Emirates
  GBR: 'GB', // United Kingdom
  USA: 'US', // United States
  UMI: 'UM', // United States Minor Outlying Islands
  URY: 'UY', // Uruguay
  UZB: 'UZ', // Uzbekistan
  VUT: 'VU', // Vanuatu
  VEN: 'VE', // Venezuela
  VNM: 'VN', // Vietnam
  VGB: 'VG', // Virgin Islands, British
  VIR: 'VI', // Virgin Islands, U.S.
  WLF: 'WF', // Wallis and Futuna
  ESH: 'EH', // Western Sahara
  YEM: 'YE', // Yemen
  ZMB: 'ZM', // Zambia
  ZWE: 'ZW', // Zimbabwe
};

// Date format mapping constants
const DATE_FORMATS: Record<string, Record<string, string>> = {
  day: {
    'DD MMM YYYY': 'DD MMM YYYY',
    'DD MMM': 'DD MMM',
    'DD MMMM YYYY': 'DD MMMM YYYY',
    'DD MMMM': 'DD MMMM',
    'MMM DD, YYYY': 'MMM DD, YYYY',
    'MMMM DD, YYYY': 'MMMM DD, YYYY',
    'DD-MM-YYYY': 'DD-MM-YYYY',
    'MM/DD/YYYY': 'MM/DD/YYYY',
  },
  week: {
    'Week of DD MMM YYYY': 'Week of DD MMM YYYY',
    'DD MMM - DD MMM YYYY': 'DD MMM - DD MMM YYYY',
    'MMM DD - DD, YYYY': 'MMM DD - DD, YYYY',
    'Week of DD MMMM YYYY': 'Week of DD MMMM YYYY',
    'DD MMMM - DD MMMM YYYY': 'DD MMMM - DD MMMM YYYY',
    'MMMM DD - DD, YYYY': 'MMMM DD - DD, YYYY',
  },
  month: {
    'MMMM YYYY': 'MMMM YYYY',
    'MMM YYYY': 'MMM YYYY',
  },
  year: {
    YYYY: 'YYYY',
  },
};

export const checkAndFormatAmountDecimals = (amount: string | number) => {
  if (typeof amount === 'number') {
    // If it's a number, ensure it has 2 decimal places
    return amount.toFixed(2);
  } else {
    {
      // And if it's already a string, check if it has decimal places
      if (amount.includes('.')) {
        // Split by decimal point
        const parts = amount.split('.');
        // If the decimal part is shorter than 2 digits, pad with zeros
        if (parts[1].length < 2) {
          return `${parts[0]}.${parts[1].padEnd(2, '0')}`;
        }
        // Otherwise use as is
        return amount;
      } else {
        // If no decimal point exists, add .00
        return `${amount}.00`;
      }
    }
  }
};

export const roundDownCurrency = (amount: number, decimalPlaces = 2): number => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(amount * factor) / factor;
};
